import { useState } from 'react'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiRequest, invalidateCache } from '../../../shared/api/base'

type NudgeFrequency = 'more' | 'default' | 'less' | 'off'

const options: { id: NudgeFrequency; labelKey: string; descKey: string }[] = [
  { id: 'more', labelKey: 'nudges.options.more', descKey: 'nudges.options.moreDesc' },
  { id: 'default', labelKey: 'nudges.options.default', descKey: 'nudges.options.defaultDesc' },
  { id: 'less', labelKey: 'nudges.options.less', descKey: 'nudges.options.lessDesc' },
  { id: 'off', labelKey: 'nudges.options.off', descKey: 'nudges.options.offDesc' },
]

export default function NudgeSettingsExpanded() {
  const { t } = useTranslation('settings')
  const [selected, setSelected] = useState<NudgeFrequency>('default')
  const [saving, setSaving] = useState(false)

  const handleSelect = async (freq: NudgeFrequency) => {
    if (freq === selected || saving) return

    const prev = selected
    setSelected(freq)
    setSaving(true)

    try {
      await apiRequest('/settings/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ nudges: { frequency: freq } }),
      })
      invalidateCache('/settings/preferences')
    } catch {
      setSelected(prev)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <p className="text-pearl/50 text-xs mb-2">{t('nudges.description')}</p>
      {options.map(opt => {
        const isSelected = selected === opt.id
        return (
          <button
            key={opt.id}
            onClick={(e) => {
              e.stopPropagation()
              handleSelect(opt.id)
            }}
            disabled={saving}
            className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
              isSelected
                ? 'bg-gold/10 border border-gold/30'
                : 'hover:bg-white/5 border border-transparent'
            } disabled:opacity-70`}
          >
            <div className="flex-1 text-left">
              <span className={`text-sm font-medium block ${
                isSelected ? 'text-ivory' : 'text-pearl/70'
              }`}>
                {t(opt.labelKey)}
              </span>
              <span className="text-pearl/60 text-xs">{t(opt.descKey)}</span>
            </div>
            {isSelected && (
              <Check size={14} className="text-gold shrink-0" />
            )}
          </button>
        )
      })}
    </div>
  )
}
